import React from 'react'
import { AxiosError } from 'axios'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import { Button, Modal, Space, Typography } from 'antd'
import {
  RefundFlowStates,
  TRefundStep
} from 'components/orders/RefundFlowModal/RefundFlowStates'
import { TPickupPointProvider } from 'models/orders'
import styles from './ChangeShipmentPoint.module.scss'

interface IShipmentPointChangeModalProps {
  open: boolean
  provider: TPickupPointProvider
  hasDelivery: boolean
  pointAddress: string
  // Заказ уже оформлен от выбранного пункта
  isApplied: boolean
  // Меняет пункт. При ошибке должен бросить исключение — по ответу сервера строится результат
  onConfirm: () => Promise<unknown>
  rereadOrder: () => Promise<unknown>
  onClose: (isChanged: boolean) => void
}

interface IFailure {
  title: string
  text: string
}

const CONFIRM_NOTE: Record<TPickupPointProvider, string> = {
  CDEK: 'Трек-номер останется прежним, штрихкод будет перевыпущен.',
  YANDEX: 'Прежняя заявка будет отменена: трек-номер и этикетка станут новыми, покупатель получит уведомление.'
}

// СДЭК подтверждает изменение накладной не сразу: бэкенд опрашивает его до 30 секунд
const LOADING_TEXT: Record<TPickupPointProvider, string> = {
  CDEK: 'Меняем пункт в накладной и ждём подтверждение СДЭК. Это может занять до минуты — не закрывайте страницу…',
  YANDEX: 'Создаём заявку от новой станции и ждём ответ Яндекс Доставки. Не закрывайте страницу…'
}

// Отказ бэкенд отдаёт с кодом 4xx ещё до записи в заказ. Обрыв связи, таймаут и 5xx
// не говорят, успела ли смена пройти
const describeFailure = (e: unknown): IFailure => {
  const { response } = e as AxiosError<{ message?: string }>
  if (response?.data?.message && response.status < 500) {
    return {
      title: 'Пункт отправления не изменён — в заказе остался прежний',
      text: response.data.message
    }
  }
  return {
    title: 'Не удалось узнать, сменился ли пункт отправления',
    text: 'Сервер не подтвердил смену. Через пару минут обновите страницу и проверьте строку «Отправка из».'
  }
}

export const ShipmentPointChangeModal: React.FC<IShipmentPointChangeModalProps> = ({
  open,
  provider,
  hasDelivery,
  pointAddress,
  isApplied,
  onConfirm,
  rereadOrder,
  onClose
}) => {
  const [step, setStep] = React.useState<TRefundStep>('confirm')
  const [failure, setFailure] = React.useState<IFailure | null>(null)

  React.useEffect(() => {
    if (open) {
      setStep('confirm')
      setFailure(null)
    }
  }, [open])

  const run = async () => {
    setStep('loading')
    try {
      await onConfirm()
    } catch (e) {
      setFailure(describeFailure(e))
      await rereadOrder().catch(() => undefined)
      setStep('error')
      return
    }
    setStep('success')
    rereadOrder()
  }

  // Ответ мог потеряться уже после смены, поэтому итог сверяем по перечитанному заказу
  const shownStep = step === 'error' && isApplied ? 'success' : step
  const isLoading = shownStep === 'loading'
  const close = () => onClose(shownStep === 'success')

  return (
    <Modal
      open={open}
      centered
      footer={null}
      width={520}
      closable={!isLoading}
      maskClosable={!isLoading}
      keyboard={!isLoading}
      onCancel={isLoading ? undefined : close}
    >
      {shownStep === 'confirm' && (
        <div className={styles.confirm}>
          <ExclamationCircleOutlined className={styles.confirmIcon} />
          <Typography.Title level={5} className={styles.confirmTitle}>
            Сменить пункт отправления
          </Typography.Title>
          <Typography.Text className={styles.line}>
            Заказ будет оформлен от пункта: {pointAddress}.
          </Typography.Text>
          {hasDelivery && (
            <Typography.Text className={styles.line}>
              {CONFIRM_NOTE[provider]}
            </Typography.Text>
          )}
          <Space className={styles.confirmActions}>
            <Button onClick={close}>Нет</Button>
            <Button type='primary' onClick={run}>
              Да
            </Button>
          </Space>
        </div>
      )}

      <RefundFlowStates
        step={shownStep}
        errorText={failure?.text ?? ''}
        loadingText={
          hasDelivery ? LOADING_TEXT[provider] : 'Сохраняем новый пункт отправления…'
        }
        successText={
          hasDelivery
            ? `Пункт отправления изменён на: ${pointAddress}. Скачайте новую этикетку, когда она появится в карточке заказа.`
            : `Пункт отправления изменён на: ${pointAddress}.`
        }
        errorTitle={failure?.title}
        onClose={close}
        onRetry={() => setStep('confirm')}
      />
    </Modal>
  )
}
